<template>
  <div class="relative w-full" ref="dropdownContainer">
    <!-- compact: the height of a native <select> (text-sm, px-2 py-1.5) for dense forms. Default look is untouched. -->
    <div class="w-full bg-surface-1 flex items-center justify-between transition-colors"
         :class="[compact ? 'px-2 rounded-md border border-border-separator' : 'px-4 rounded border border-border-default', disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-border-default', { 'border-brand-outline': is_open && !disabled }]" @click.stop="toggleDropdown">
      <div class="flex-1 min-w-0 flex items-center" :class="compact ? 'py-1.5' : 'h-11'">
        <div class="text-sm leading-4 tracking-tight select-none" :class="compact ? 'text-text-default truncate' : 'text-text-default/80 font-medium'">
          {{ display_value }}<span v-if="display_hint" class="ml-1 italic font-normal text-text-subtle">{{ display_hint }}</span>
        </div>
      </div>
      <i class="ri-arrow-down-s-line text-text-subtle transition-transform duration-200" :class="[compact ? 'text-base' : 'text-lg', { 'rotate-180': is_open }]" />
    </div>
    <div v-if="is_open" ref="dropdownPanel" class="dropdown-panel absolute z-50 mt-1 w-full bg-surface-1 border border-border-default shadow-lg" :class="compact ? 'rounded-md' : 'rounded'" @click.stop>
      <template v-for="option in sorted_options" :key="getOptionKey(option)">
        <!-- { heading: 'Groups' } renders a non-selectable group label, the <optgroup> equivalent. A sorted list sorts within each heading's group. -->
        <div v-if="isHeading(option)" class="pt-2 pb-1 text-[11px] uppercase tracking-wide text-text-subtle select-none" :class="compact ? 'px-2' : 'px-4'">{{ option.heading }}</div>
        <!-- { disabled: true } shows an option (and lets it display as the current value) without letting it be chosen. -->
        <div v-else class="transition-colors" :class="[compact ? 'px-2 py-1.5' : 'px-4 py-2.5', isDisabledOption(option) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-surface-2', { 'bg-brand-subtle': isSelected(option) }]" :data-selected="isSelected(option) ? 'true' : null" @click.stop="selectOption(option)">
          <div class="text-sm leading-4 tracking-tight select-none" :class="isSelected(option) ? 'text-text-link font-medium' : (compact ? 'text-text-default' : 'text-text-default/80')">
            {{ getOptionLabel(option) }}<span v-if="getOptionHint(option)" class="ml-1 italic font-normal text-text-subtle">{{ getOptionHint(option) }}</span>
          </div>
        </div>
      </template>
      <div v-if="sorted_options.length === 0" class="py-2.5 text-text-placeholder text-sm" :class="compact ? 'px-2' : 'px-4'">{{ emptyText ?? $t('common.dropdown.noOptions') }}</div>
    </div>
  </div>
</template>


<style scoped>
.dropdown-panel {
  max-height: 240px;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  scrollbar-color: var(--gray-30) transparent;
}
.dropdown-panel::-webkit-scrollbar { width: 6px; }
.dropdown-panel::-webkit-scrollbar-track { background: transparent; }
.dropdown-panel::-webkit-scrollbar-thumb { background-color: var(--gray-30); border-radius: 3px; }
.dropdown-panel::-webkit-scrollbar-thumb:hover { background-color: var(--gray-50); }
</style>

<script>
// Breathing room kept between the bottom of an opened panel and the bottom edge of the
// scrolling ancestor, so the last option never sits flush against the fold.
const PANEL_VIEW_GUTTER = 5;

export default {
  name: 'FusionDropdown',
  props: {
    modelValue: {
      type: [String, Number, Object],
      default: null
    },
    options: {
      type: Array,
      required: true,
      default: () => []
    },
    labelKey: {
      type: String,
      default: 'label'
    },
    // Optional secondary text rendered italic and muted after the label, for a caveat that belongs
    // WITH an option rather than in the field's description -- "(English only)" on the date formats
    // whose shape only applies to an English answer.
    //
    // A separate field rather than something appended to the label, because a label may be pinned:
    // ReyPreferences asserts its five date-format labels are the literal example dates and are never
    // translated, and concatenating a localized caveat into them would break that contract.
    hintKey: {
      type: String,
      required: false,
      default: 'hint'
    },
    valueKey: {
      type: String,
      default: 'value'
    },
    placeholder: {
      type: String,
      default: null
    },
    // What an empty panel says. A prop like placeholder so a localized form can pass its own $t text;
    // the English default keeps every existing caller as it was.
    // Merge with FUS-76: null falls back to the localized common.dropdown.noOptions at render time,
    // the same way placeholder does, so the built-in default follows the user's language.
    emptyText: {
      type: String,
      required: false,
      default: null
    },
    disabled: {
      type: Boolean,
      default: false
    },
    sortOrder: {
      type: String,
      default: 'asc' // 'asc', 'desc', or 'none'
    },
    // Slim, native-<select>-height variant for dense forms (the scheduled-task screens).
    compact: {
      type: Boolean,
      default: false
    }
  },
  beforeUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside);
  },
  // 'change' fires after 'update:modelValue', and only when the pick differs from the current value --
  // the semantics of a native <select>'s @change, so a form can swap a <select> for this without
  // re-saving on a re-pick of the same option.
  emits: ['update:modelValue', 'change'],
  data() {
    return {
      is_open: false
    };
  },
  computed: {
    // Sorted within each heading's group, never across: a heading has no label to sort by (sorting it with
    // the rows threw), and it has to stay directly above the rows it labels. A list with no headings is one
    // group, so it sorts exactly as before.
    sorted_options() {
      if (this.sortOrder === 'none') return this.options;
      const groups = [{ heading: null, rows: [] }];
      this.options.forEach((option) => {
        if (this.isHeading(option)) groups.push({ heading: option, rows: [] });
        else groups[groups.length - 1].rows.push(option);
      });
      return groups.flatMap(g => (g.heading ? [g.heading] : []).concat(this.sortRows(g.rows)));
    },
    display_value() {
      // Caller-supplied placeholder wins; the built-in default is localized at render time so it
      // follows a live language switch (a prop default would freeze it at app startup).
      const placeholder = this.placeholder ?? this.$t('common.dropdown.placeholder');
      if (this.modelValue === null || this.modelValue === undefined) {
        return placeholder;
      }
      const selectedOption = this.options.find(opt => this.getOptionValue(opt) === this.modelValue);
      return selectedOption ? this.getOptionLabel(selectedOption) : placeholder;
    },
    // The selected option's hint, so the caveat stays visible once the panel is closed. Empty when
    // the placeholder is showing -- a placeholder has no option behind it to carry one.
    display_hint() {
      if (this.modelValue === null || this.modelValue === undefined) {
        return '';
      }
      const selectedOption = this.options.find(opt => this.getOptionValue(opt) === this.modelValue);
      return selectedOption ? this.getOptionHint(selectedOption) : '';
    }
  },
  methods: {
    toggleDropdown() {
      if (this.disabled) return;
      this.is_open = !this.is_open;
    },
    selectOption(option) {
      // A panel opened before the dropdown was disabled (a save started while it was open) must not still
      // take a pick: v-model would move to a value the caller's save then skips. Closed rather than left
      // open, since nothing in it can be chosen until the dropdown is enabled again.
      if (this.disabled) {
        this.is_open = false;
        return;
      }
      // A disabled option swallows the click but leaves the panel open, as a native one does.
      if (this.isDisabledOption(option)) return;
      const value = this.getOptionValue(option);
      const changed = value !== this.modelValue;
      this.$emit('update:modelValue', value);
      if (changed) this.$emit('change', value);
      this.is_open = false;
    },
    sortRows(rows) {
      const sorted = [...rows].sort((a, b) => {
        const labelA = this.getOptionLabel(a).toLowerCase();
        const labelB = this.getOptionLabel(b).toLowerCase();
        return labelA.localeCompare(labelB);
      });
      return this.sortOrder === 'desc' ? sorted.reverse() : sorted;
    },
    isHeading(option) {
      return typeof option === 'object' && option !== null && option.heading !== undefined;
    },
    isDisabledOption(option) {
      return typeof option === 'object' && option !== null && !!option.disabled;
    },
    getOptionKey(option) {
      return this.isHeading(option) ? `heading:${option.heading}` : this.getOptionValue(option);
    },
    getOptionLabel(option) {
      if (typeof option === 'string' || typeof option === 'number') {
        return option;
      }
      return option[this.labelKey];
    },
    getOptionValue(option) {
      if (typeof option === 'string' || typeof option === 'number') {
        return option;
      }
      return option[this.valueKey];
    },
    // A bare string or number option has nowhere to put a hint, so it simply has none.
    getOptionHint(option) {
      if (typeof option === 'string' || typeof option === 'number') {
        return '';
      }
      return option[this.hintKey] || '';
    },
    isSelected(option) {
      return this.getOptionValue(option) === this.modelValue;
    },
    // A long list (the 96 times of day) opened at its top would hide the current value far below the
    // fold, so the panel scrolls itself to centre the selected row — only when that row is out of view,
    // so a short list that already shows it does not move. The panel is the rows' offsetParent (it is
    // absolutely positioned), which is what makes offsetTop the row's position inside it.
    scrollSelectedIntoView() {
      const panel = this.$refs.dropdownPanel;
      if (!panel) return;
      const row = panel.querySelector('[data-selected="true"]');
      if (!row) return;

      const top = row.offsetTop;
      const bottom = top + row.offsetHeight;
      if (top >= panel.scrollTop && bottom <= panel.scrollTop + panel.clientHeight) return;
      panel.scrollTop = Math.max(0, top - (panel.clientHeight - row.offsetHeight) / 2);
    },
    // The panel is absolutely positioned, so opening it low in a scrollable ancestor (a modal
    // body, for instance) leaves the tail of the list clipped below the fold. Scroll that
    // ancestor by the smallest amount that brings the whole panel into view.
    scrollPanelIntoView() {
      const panel = this.$refs.dropdownPanel;
      if (!panel) return;

      const scroller = this.findScrollParent(panel);
      if (!scroller) return;

      const isDocumentScroller = scroller === document.scrollingElement || scroller === document.documentElement;
      const scrollerBottom = isDocumentScroller ? window.innerHeight : scroller.getBoundingClientRect().bottom;
      const overflow = panel.getBoundingClientRect().bottom - scrollerBottom + PANEL_VIEW_GUTTER;
      if (overflow <= 0) return;

      const top = scroller.scrollTop + overflow;
      if (typeof scroller.scrollTo === 'function')
        scroller.scrollTo({ top, behavior: 'smooth' });
      else
        scroller.scrollTop = top;
    },
    // Nearest scrolling ancestor - that is the element clipping the panel. Starts above the
    // panel so the panel's own overflow-y is not what matches. Falls back to the document
    // scroller for a dropdown that sits outside any scrollable panel.
    findScrollParent(el) {
      let node = el.parentElement;
      while (node && node !== document.body) {
        const overflowY = window.getComputedStyle(node).overflowY;
        if (overflowY === 'auto' || overflowY === 'scroll')
          return node;
        node = node.parentElement;
      }
      return document.scrollingElement || document.documentElement;
    },
    handleClickOutside(event) {
      const container = this.$refs.dropdownContainer;
      if (container && !container.contains(event.target)) {
        this.is_open = false;
      }
    }
  },
  watch: {
    is_open(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          document.addEventListener('mousedown', this.handleClickOutside);
          this.scrollSelectedIntoView();
          this.scrollPanelIntoView();
        });
      } else {
        document.removeEventListener('mousedown', this.handleClickOutside);
      }
    }
  },
};
</script>
